import { useState, useRef, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api';
import BuildingPageHeading from '../../components/BuildingPageHeading';
import FloorPlanCanvas from '../../components/FloorPlanCanvas';
import FloorPlanChangeUnitIdPanel from '../../components/FloorPlanChangeUnitIdPanel';
import SsUnitDetailModal from '../../components/SsUnitDetailModal';
import toast from 'react-hot-toast';
import { asList } from '../../utils/asList';
import { nextAutoUniqueId, nextAutoUniqueIdForPrefix, unitMatchesEquipmentType } from '../../utils/unitIdFormat';
import SsEquipmentTypePool from '../../components/SsEquipmentTypePool';
import UnitInspectionListPopup from '../../components/UnitInspectionListPopup';
import { formatSsLabel } from '../../utils/unitDisplay';
import { sortBySsCode } from '../../utils/ssCodeSort';
import {
  filterInstancesForInspectionScope,
} from '../../utils/inspectionScope';

export default function BuildingFloorPlan() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [highlightUnitId, setHighlightUnitId] = useState(null);
  const [editUnit, setEditUnit] = useState(null);
  const [localUnits, setLocalUnits] = useState(null);
  const [selectedLevelId, setSelectedLevelId] = useState('');
  const [selectedBlockId, setSelectedBlockId] = useState('');
  const [selectedInstanceId, setSelectedInstanceId] = useState(null);
  const [selectedEquipmentTypeId, setSelectedEquipmentTypeId] = useState(null);
  const [showAllSubs, setShowAllSubs] = useState(false);
  const [showTypePool, setShowTypePool] = useState(false);
  const [listPopupUnit, setListPopupUnit] = useState(null);
  const [editHubTab, setEditHubTab] = useState('system-info');
  const placingRef = useRef(false);

  const { data: building, isLoading: buildingLoading } = useQuery({
    queryKey: ['building', id],
    queryFn: async () => (await api.get(`/buildings/${id}`)).data,
  });

  const { data: instances = [] } = useQuery({
    queryKey: ['building', id, 'ss-instances'],
    queryFn: async () => {
      const response = await api.get(`/buildings/${id}/ss-instances`);
      const raw = response.data?.data ?? response.data;
      return Array.isArray(raw) ? raw : [];
    },
    enabled: !!id,
  });
  const sortedInstances = useMemo(
    () => sortBySsCode(filterInstancesForInspectionScope(instances, building)),
    [instances, building]
  );

  const scopedSsCodes = useMemo(
    () =>
      new Set(
        sortedInstances.map((i) => String(i.ss_code || '').trim()).filter(Boolean)
      ),
    [sortedInstances]
  );

  const scopedInstanceIds = useMemo(
    () => new Set(sortedInstances.map((i) => Number(i.id))),
    [sortedInstances]
  );

  const { data: levelsSetup } = useQuery({
    queryKey: ['building', id, 'levels-setup'],
    queryFn: async () => (await api.get(`/buildings/${id}/levels-setup`)).data?.data,
    enabled: !!id,
  });

  const { data: floorSetupStatus } = useQuery({
    queryKey: ['building', id, 'floor-setup-status'],
    queryFn: async () =>
      (await api.get(`/buildings/${id}/levels-setup/status`)).data?.data,
    enabled: !!id,
  });

  const structureReady = !!floorSetupStatus?.floor_setup_complete;

  /** All units including inactive ?? used for next free ID (never reuse). */
  const { data: unitsData = [], isLoading: unitsLoading } = useQuery({
    queryKey: ['building', id, 'ss-units', 'all'],
    queryFn: async () => {
      const response = await api.get(`/buildings/${id}/ss-units`, {
        params: { includeInactive: '1' },
      });
      const raw = response.data?.data ?? response.data;
      return asList(raw);
    },
    enabled: !!id,
  });

  const { data: paperworkPack } = useQuery({
    queryKey: ['building', id, 'paperwork'],
    queryFn: async () => (await api.get(`/buildings/${id}/paperwork`)).data?.data,
    enabled: !!id,
  });

  /** Latest issued paperwork unit outcomes → colour sub markers on the plan. */
  const inspectOutcomeByKey = useMemo(() => {
    const map = new Map();
    const rows = Array.isArray(paperworkPack?.rows) ? paperworkPack.rows : [];
    rows.forEach((row) => {
      const units = Array.isArray(row?.units) ? row.units : [];
      units.forEach((u) => {
        const outcome = String(u?.outcome || '').trim();
        if (!outcome) return;
        const keys = [
          u.unitId,
          u.unit_id,
          u.id,
          u.label,
          u.uniqueId,
          u.unique_id,
        ]
          .map((k) => String(k || '').trim())
          .filter(Boolean);
        keys.forEach((k) => map.set(k, outcome));
      });
    });
    return map;
  }, [paperworkPack]);

  const invalidateLevels = () => {
    queryClient.invalidateQueries(['building', id, 'levels-setup']);
    queryClient.invalidateQueries(['building', id, 'floor-setup-status']);
    queryClient.invalidateQueries(['building', id]);
  };

  const structureEditLink = `/buildings/${id}/edit#floor-structure`;

  useEffect(() => {
    setLocalUnits(null);
  }, [unitsData]);

  const unitsAll = useMemo(() => {
    const raw = Array.isArray(localUnits ?? unitsData) ? localUnits ?? unitsData : [];
    return raw.filter((u) => {
      const code = String(u.ss_code || '').trim();
      if (code && scopedSsCodes.has(code)) return true;
      return scopedInstanceIds.has(Number(u.building_ss_instance_id));
    });
  }, [localUnits, unitsData, scopedSsCodes, scopedInstanceIds]);

  /** Active (+ drafts) for markers on the plan. Inactive kept only for ID allocation. */
  const unitsActive = useMemo(
    () =>
      unitsAll.filter((u) => String(u.status || 'ACTIVE').toUpperCase() !== 'INACTIVE'),
    [unitsAll]
  );

  const selectedInstance = useMemo(
    () => instances.find((i) => String(i.id) === String(selectedInstanceId)) || null,
    [instances, selectedInstanceId]
  );

  useEffect(() => {
    setShowTypePool(false);
    setShowAllSubs(false);
  }, [selectedInstanceId]);

  /** Keep a valid sub-type selected whenever this SS has registered types. */
  useEffect(() => {
    const types = selectedInstance?.equipment_types || [];
    if (!types.length) {
      setSelectedEquipmentTypeId(null);
      return;
    }
    setSelectedEquipmentTypeId((prev) => {
      if (prev != null && types.some((t) => String(t.id) === String(prev))) return prev;
      return types[0].id;
    });
  }, [selectedInstance]);

  const unitsForSelectedSs = useMemo(() => {
    if (!selectedInstanceId) return [];
    return unitsActive.filter((u) => {
      if (String(u.building_ss_instance_id) === String(selectedInstanceId)) return true;
      if (u.building_ss_instance_id) return false;
      if (!selectedInstance) return false;
      return (
        String(u.ss_code || '')
          .replace(/\s+/g, ' ')
          .trim()
          .toUpperCase() ===
        String(selectedInstance.ss_code || '')
          .replace(/\s+/g, ' ')
          .trim()
          .toUpperCase()
      );
    });
  }, [unitsActive, selectedInstanceId, selectedInstance]);

  const numberingUnitsForSs = useMemo(() => {
    if (!selectedInstanceId) return unitsAll;
    return unitsAll.filter((u) => {
      if (String(u.building_ss_instance_id) === String(selectedInstanceId)) return true;
      if (u.building_ss_instance_id) return false;
      if (!selectedInstance) return false;
      return (
        String(u.ss_code || '')
          .replace(/\s+/g, ' ')
          .trim()
          .toUpperCase() ===
        String(selectedInstance.ss_code || '')
          .replace(/\s+/g, ' ')
          .trim()
          .toUpperCase()
      );
    });
  }, [unitsAll, selectedInstanceId, selectedInstance]);

  useEffect(() => {
    if (!instances.length) return;
    const still = instances.some((i) => String(i.id) === String(selectedInstanceId));
    if (!still) setSelectedInstanceId(String(instances[0].id));
  }, [instances, selectedInstanceId]);

  const blockOptions = Array.isArray(levelsSetup?.blocks) ? levelsSetup.blocks : [];
  const levelOptions = useMemo(() => {
    const all = Array.isArray(levelsSetup?.levels) ? levelsSetup.levels : [];
    if (!selectedBlockId) return all;
    return all.filter((l) => String(l.block_id) === String(selectedBlockId));
  }, [levelsSetup?.levels, selectedBlockId]);

  /** One Place control = former Part (block) + Level drawing sheet */
  const placeOptions = useMemo(() => {
    const blocks = Array.isArray(levelsSetup?.blocks) ? levelsSetup.blocks : [];
    const levels = Array.isArray(levelsSetup?.levels) ? levelsSetup.levels : [];
    const byBlock = new Map(blocks.map((b) => [String(b.id), b]));
    const countByBlock = levels.reduce((m, l) => {
      const k = String(l.block_id || '');
      m[k] = (m[k] || 0) + 1;
      return m;
    }, {});
    return levels.map((l) => {
      const b = byBlock.get(String(l.block_id));
      const group = (b?.building_group_label && String(b.building_group_label).trim()) || '';
      const part = (b && (b.label || b.code)) || '';
      const partLabel = group ? `${group} · ${part}` : part || 'Place';
      const multiFloor = (countByBlock[String(l.block_id)] || 0) > 1;
      const label = multiFloor ? `${partLabel} · ${l.label}` : partLabel;
      return {
        levelId: String(l.id),
        blockId: l.block_id != null ? String(l.block_id) : '',
        label: b?.code && !String(label).includes(`(${b.code})`) ? `${label}${b.code ? ` (${b.code})` : ''}` : label,
      };
    });
  }, [levelsSetup?.blocks, levelsSetup?.levels]);

  useEffect(() => {
    if (!selectedBlockId && blockOptions.length) {
      setSelectedBlockId(String(blockOptions[0].id));
    }
  }, [blockOptions, selectedBlockId]);

  useEffect(() => {
    if (!levelOptions.length) {
      setSelectedLevelId('');
      return;
    }
    const still = levelOptions.some((l) => String(l.id) === String(selectedLevelId));
    if (!still) setSelectedLevelId(String(levelOptions[0].id));
  }, [levelOptions, selectedLevelId]);

  const selectedLevel = levelOptions.find((l) => String(l.id) === String(selectedLevelId));

  const onPlaceChange = (levelId) => {
    const opt = placeOptions.find((p) => String(p.levelId) === String(levelId));
    if (!opt) {
      setSelectedLevelId(levelId);
      return;
    }
    if (opt.blockId) setSelectedBlockId(opt.blockId);
    setSelectedLevelId(opt.levelId);
  };

  const floorPlanUrl = useMemo(() => {
    if (selectedLevel?.plan_url) return selectedLevel.plan_url;
    const plans = Array.isArray(levelsSetup?.plans) ? levelsSetup.plans : [];
    const planId = selectedLevel?.floor_plan_id || selectedLevel?.plan_id;
    if (planId) {
      const plan = plans.find((p) => String(p.id) === String(planId));
      if (plan?.plan_url) return plan.plan_url;
    }
    // Multi-Place: never fall back to building URL (shared sketch across floors).
    const levelCount = Array.isArray(levelsSetup?.levels) ? levelsSetup.levels.length : 0;
    if (levelCount > 1) return '';
    return building?.floor_plan_url || '';
  }, [selectedLevel, levelsSetup?.plans, levelsSetup?.levels, building?.floor_plan_url]);

  const units = useMemo(() => {
    let rows = unitsForSelectedSs;
    if (selectedLevelId && levelOptions.length) {
      rows = rows.filter((u) => {
        // Strict Place filter — units without level_id stay off the sheet
        if (u.level_id == null || u.level_id === '') return false;
        return String(u.level_id) === String(selectedLevelId);
      });
    }
    const types = selectedInstance?.equipment_types || [];
    if (types.length && !showAllSubs) {
      const type = types.find((t) => String(t.id) === String(selectedEquipmentTypeId));
      if (type) rows = rows.filter((u) => unitMatchesEquipmentType(u, type));
    }
    return rows.map((u) => {
      const keys = [u.id, u.unique_id, u.name]
        .map((k) => String(k || '').trim())
        .filter(Boolean);
      let outcome = '';
      for (const k of keys) {
        if (inspectOutcomeByKey.has(k)) {
          outcome = inspectOutcomeByKey.get(k);
          break;
        }
      }
      if (!outcome) return u;
      return { ...u, outcome, _inspectStatus: outcome };
    });
  }, [
    unitsForSelectedSs,
    selectedLevelId,
    levelOptions.length,
    selectedInstance,
    showAllSubs,
    selectedEquipmentTypeId,
    inspectOutcomeByKey,
  ]);

  const usedUniqueIds = useMemo(
    () =>
      unitsAll
        .filter((u) => {
          const st = String(u?.status || 'ACTIVE').toUpperCase();
          if (st === 'INACTIVE' || st === 'PURGED') return false;
          const id = String(u?.unique_id || '');
          return id && !id.startsWith('__');
        })
        .map((u) => u.unique_id),
    [unitsAll]
  );

  /** Click drawing = persist next numbered unit for the selected sub-type.
   *  Shared TYPICAL drawings (each level): place once → same xy on every floor,
   *  unique_ids ascending from lowest floor upward. */
  const handlePlaceUnit = async (instanceId, pos) => {
    if (!pos || placingRef.current) return;
    const inst = instances.find((i) => String(i.id) === String(instanceId));
    if (!inst) {
      toast.error('Select an SS first');
      return;
    }
    const types = inst.equipment_types || [];
    let type = types.find((t) => String(t.id) === String(selectedEquipmentTypeId));
    if (types.length && !type) {
      type = types[0];
      setSelectedEquipmentTypeId(type.id);
    }
    placingRef.current = true;

    const allLevels = Array.isArray(levelsSetup?.levels) ? levelsSetup.levels : [];
    const blocks = Array.isArray(levelsSetup?.blocks) ? levelsSetup.blocks : [];
    const cur = selectedLevel || allLevels.find((l) => String(l.id) === String(selectedLevelId));
    const planId = cur?.floor_plan_id || cur?.plan_id;
    const blockMeta = blocks.find((b) => String(b.id) === String(cur?.block_id));
    const isEachMode =
      !blockMeta || String(blockMeta.level_mode || 'EACH').toUpperCase() !== 'SINGLE';

    let targetLevels = cur ? [cur] : [];
    if (isEachMode && planId && cur) {
      const siblings = allLevels
        .filter(
          (l) =>
            String(l.block_id || '') === String(cur.block_id || '') &&
            String(l.floor_plan_id || l.plan_id || '') === String(planId)
        )
        .sort((a, b) => {
          const sa = Number(a.sort_order);
          const sb = Number(b.sort_order);
          if (Number.isFinite(sa) && Number.isFinite(sb) && sa !== sb) return sa - sb;
          return String(a.code || '').localeCompare(String(b.code || ''), undefined, {
            numeric: true,
          });
        });
      if (siblings.length > 1) targetLevels = siblings;
    }

    const typeName = type ? String(type.label || '').trim() : '';
    let workingUnits = [...unitsAll];
    const createdList = [];

    try {
      for (const level of targetLevels) {
        const suggestedId = type
          ? nextAutoUniqueIdForPrefix(type.prefix, workingUnits)
          : nextAutoUniqueId(inst.ss_code, workingUnits);
        const res = await api.post(`/buildings/${id}/ss-instances/${inst.id}/units`, {
          unique_id: suggestedId,
          // Name tracks unit ID so renumbering on Floor plan keeps Name in sync
          name: suggestedId,
          unit_type: typeName || undefined,
          equipment_type_id: type?.id || undefined,
          location: inst.location || '',
          floor_plan_x: pos.x,
          floor_plan_y: pos.y,
          level_id: level.id || null,
        });
        const created = res.data?.data || res.data;
        if (created && created.id) {
          const row = {
            ...created,
            floor_plan_x: created.floor_plan_x ?? pos.x,
            floor_plan_y: created.floor_plan_y ?? pos.y,
            building_ss_instance_id: created.building_ss_instance_id ?? inst.id,
            equipment_type_id: created.equipment_type_id ?? type?.id ?? null,
            ss_code: created.ss_code || inst.ss_code,
            status: created.status || 'ACTIVE',
            unique_id: created.unique_id || suggestedId,
            name: created.name || created.unique_id || suggestedId,
            level_id: created.level_id ?? level.id,
          };
          createdList.push(row);
          workingUnits = [...workingUnits, row];
        }
      }

      try {
        await api.post('/checklist-templates/build-default', { ssCode: inst.ss_code });
      } catch {
        /* optional — unit is already saved; checklist can be built from SS Units */
      }

      if (createdList.length) {
        queryClient.setQueryData(['building', id, 'ss-units', 'all'], (old) => {
          const list = asList(old);
          const next = [...list];
          for (const row of createdList) {
            if (!next.some((u) => String(u.id) === String(row.id))) next.push(row);
          }
          return next;
        });
      }
      await queryClient.invalidateQueries({ queryKey: ['building', id, 'ss-units'] });
      const first = createdList[0];
      const last = createdList[createdList.length - 1];
      setHighlightUnitId(first?.id || null);
      if (createdList.length > 1) {
        toast.success(
          `Added ${createdList.length} units (${first?.unique_id} … ${last?.unique_id}) across floors`
        );
      } else {
        toast.success(`Added ${first?.unique_id || 'unit'}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to add unit');
    } finally {
      placingRef.current = false;
    }
  };

  const handleDeleteUnit = async (unit, opts = {}) => {
    if (!unit) return;
    const label = unit.unique_id || unit.name || 'this unit';
    const hard =
      !!opts.hard || String(unit.status || '').toUpperCase() === 'INACTIVE';
    if (
      !opts.skipConfirm &&
      !window.confirm(
        hard
          ? `Remove ${label} from the list?`
          : `Delete ${label}?`
      )
    ) {
      return;
    }
    try {
      const res = await api.delete(`/ss-units/${encodeURIComponent(unit.id)}`, {
        params: hard ? { hard: '1' } : undefined,
      });
      if (editUnit?.id === unit.id) setEditUnit(null);
      if (String(highlightUnitId) === String(unit.id)) setHighlightUnitId(null);
      await queryClient.invalidateQueries({ queryKey: ['building', id, 'ss-units'] });
      toast.success(
        hard || res.data?.hard
          ? `Removed ${label} from list`
          : `Deleted ${label} (inactive)`
      );
    } catch (e) {
      console.error('[delete unit]', e);
      toast.error(e.response?.data?.error || e.message || 'Failed to delete unit');
    }
  };

  const handleUnitClick = async (unit) => {
    if (!unit) return;
    setHighlightUnitId(unit.id);
    try {
      const res = await api.get(`/ss-units/${unit.id}`);
      setEditUnit(res.data?.data ?? res.data);
    } catch {
      setEditUnit(unit);
    }
  };

  const handleMoveUnit = async (unitId, x, y) => {
    setLocalUnits((prev) => {
      const base = prev ?? unitsData;
      return base.map((u) => (u.id === unitId ? { ...u, floor_plan_x: x, floor_plan_y: y } : u));
    });
    try {
      await api.patch(`/ss-units/${unitId}/floor-plan-location`, { x, y });
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save position');
      setLocalUnits(null);
      await queryClient.invalidateQueries({ queryKey: ['building', id, 'ss-units'] });
    }
  };

  const handleRemoveFromPlan = async (unit) => {
    try {
      await api.put(`/ss-units/${unit.id}`, { floor_plan_x: null, floor_plan_y: null });
      queryClient.invalidateQueries(['building', id, 'ss-units']);
      setEditUnit(null);
      toast.success('Removed from plan');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to remove from plan');
    }
  };

  if (buildingLoading || unitsLoading) {
    return <div className="text-center py-12 text-gray-500">Loading…</div>;
  }

  if (!building) {
    return <div className="text-center py-12">Building not found</div>;
  }

  if (!structureReady) {
    return (
      <div className="space-y-4">
        <BuildingPageHeading
          buildingId={id}
          buildingName={building.name}
          title="Floor plan"
          infoTitle="Floor plan"
          infoChildren={
            <p className="text-sm">
              Set building structure and drawings in Building settings first, then place SS units here.
            </p>
          }
        />
        <div className="bg-card rounded-xl border border-amber-200 p-6 max-w-xl space-y-3">
          <h2 className="text-base font-semibold text-gray-900">Structure setup required</h2>
          <p className="text-sm text-gray-600">
            This tab places SS units on the plan. It opens only after structure setup is marked
            complete (<code className="text-xs">floor_setup_complete</code>). Drawings may already
            exist for the worker app — the app does not wait for that flag.
          </p>
          {floorSetupStatus?.has_structure ? (
            <p className="text-sm text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              Structure (parts/floors) is already on file
              {floorSetupStatus?.levels_with_plan
                ? ` · ${floorSetupStatus.levels_with_plan} level(s) with a drawing`
                : ''}
              . Use <strong>Use existing structure</strong> to unlock unit placement here.
            </p>
          ) : (
            <p className="text-sm text-gray-600">
              Building parts, floors, and drawings are managed in <strong>Edit Building</strong>.
              Complete setup there, then return.
            </p>
          )}
          <div className="flex flex-wrap gap-2 pt-1">
            <Link
              to={`/buildings/${id}/edit#floor-structure`}
              className="inline-flex items-center px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold"
            >
              Edit Building
            </Link>
            {floorSetupStatus?.has_structure ? (
              <button
                type="button"
                onClick={async () => {
                  try {
                    await api.put(`/buildings/${id}/levels-setup/status`, {
                      floor_setup_complete: true,
                      floor_setup_draft: false,
                    });
                    toast.success('Marked complete');
                    invalidateLevels();
                  } catch (e) {
                    toast.error(e.response?.data?.error || 'Failed');
                  }
                }}
                className="inline-flex items-center px-4 py-2 rounded-lg border text-sm font-semibold bg-white"
              >
                Use existing structure
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-5rem)] -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
      <div className="shrink-0 mb-3">
        <BuildingPageHeading
          buildingId={id}
          buildingName={building.name}
          title="Floor plan"
          infoTitle="Floor plan"
          infoChildren={
            <div className="space-y-2 text-sm">
              <p className="font-medium">{building.name}</p>
              <p>
                Pick a building part and level to show its drawing. Upload drawings in Edit Building; here you only
                place SS units on the sheet. Edit unit IDs in the panel on the right.
              </p>
            </div>
          }
        />
      </div>

      {selectedInstance && (
        <div className="shrink-0 mb-3">
          <button
            type="button"
            onClick={() => setShowTypePool((v) => !v)}
            className="text-xs font-medium text-primary-700 hover:underline"
          >
            {showTypePool ? 'Hide equipment types' : 'Register equipment types for this SS'}
          </button>
          {showTypePool ? (
            <div className="mt-2">
              <SsEquipmentTypePool
                buildingId={id}
                instanceId={selectedInstance.id}
                ssCode={selectedInstance.ss_code}
              />
            </div>
          ) : null}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-3 flex-1 min-h-0">
        <div className="bg-card rounded-lg shadow p-3 md:p-4 flex flex-col flex-1 min-h-0 min-w-0">
          <div className="flex flex-wrap items-center gap-3 mb-3 pb-3 border-b border-gray-100 shrink-0">
            {placeOptions.length > 0 && (
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <span className="font-medium text-gray-500">Place</span>
                <select
                  value={selectedLevelId || ''}
                  onChange={(e) => onPlaceChange(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg bg-white max-w-[22rem] text-sm"
                >
                  {placeOptions.map((p) => (
                    <option key={p.levelId} value={p.levelId}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <Link
              to={structureEditLink}
              className="text-xs text-primary-700 hover:underline ml-auto"
            >
              Upload drawings in Edit Building
            </Link>
          </div>

          <FloorPlanCanvas
            floorPlanUrl={floorPlanUrl}
            units={units}
            numberingUnits={numberingUnitsForSs}
            ssInstances={sortedInstances}
            highlightUnitId={highlightUnitId}
            selectedInstanceId={selectedInstanceId}
            onSelectInstance={(instId) => instId && setSelectedInstanceId(String(instId))}
            selectedEquipmentTypeId={selectedEquipmentTypeId}
            onSelectEquipmentType={(typeId) => setSelectedEquipmentTypeId(typeId)}
            showAllSubs={showAllSubs}
            onShowAllSubsChange={setShowAllSubs}
            onPlaceUnit={handlePlaceUnit}
            onMoveUnit={handleMoveUnit}
            onDeleteUnit={handleDeleteUnit}
            onUnitClick={handleUnitClick}
            onUnitNameClick={(unit) => setListPopupUnit(unit)}
            onHighlightUnit={setHighlightUnitId}
            onManagePlans={() => {
              window.location.href = structureEditLink;
            }}
          />
        </div>
        <FloorPlanChangeUnitIdPanel
          buildingId={id}
          unitsAll={unitsAll}
          ssInstances={sortedInstances}
          selectedInstanceId={selectedInstanceId}
          onSelectInstance={setSelectedInstanceId}
          selectedEquipmentTypeId={selectedEquipmentTypeId}
          showAllSubs={showAllSubs}
          highlightUnitId={highlightUnitId}
          onHighlightUnit={setHighlightUnitId}
          onDeleteUnit={handleDeleteUnit}
          onOpenUnit={handleUnitClick}
          onOpenInspectionList={(unit) => setListPopupUnit(unit)}
          lockSsFilter
          className="lg:w-[20rem] xl:w-[22rem]"
        />
      </div>

      {editUnit && (
        <SsUnitDetailModal
          key={editUnit.id}
          unit={editUnit}
          building={building}
          hubTab={editHubTab}
          onHubTabChange={setEditHubTab}
          allowedHubTabs={['system-info']}
          onClose={() => {
            setEditUnit(null);
            setEditHubTab('system-info');
          }}
          usedUniqueIds={usedUniqueIds}
          idSuffixOnly
          showRemoveFromPlan
          onRemoveFromPlan={handleRemoveFromPlan}
          onDeleted={() => {
            setEditUnit(null);
            queryClient.invalidateQueries(['building', id, 'ss-units']);
          }}
        />
      )}

      <UnitInspectionListPopup
        unit={listPopupUnit}
        isOpen={!!listPopupUnit}
        onClose={() => setListPopupUnit(null)}
        onEdit={async (unit) => {
          // Floor plan: open Set System only — inspection list / Update Info not available here
          setListPopupUnit(null);
          setEditHubTab('system-info');
          try {
            const res = await api.get(`/ss-units/${unit.id}`);
            setEditUnit(res.data?.data ?? res.data);
          } catch {
            setEditUnit(unit);
          }
        }}
      />
    </div>
  );
}
